import React, { useState, useContext, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Button from '@material-ui/core/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Threshold from './Threshold';
import {
	setRuleType,
	createRule,
	updateSelectedItem,
	updateBulkItemSearchResults,
	setIsCreateRuleOpen,
	setIsWatchListOpen,
	createRuleFailure,
	updateCompareSelectedItemA,
	updateCompareSelectedItemB,
} from '../../redux/actions/createRuleActions';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles, styles, textStyle, selectStyleMenu } from './styles';
import {
	THRESHOLD,
	COMPARISON,
	NEED_TO_BE_SELECTED,
	BOGLE_LIGHT,
	CREATE_RULE,
	productFilters,
	EDIT_RULE,
	LEFT_START,
} from '../../constants/constant';
import Comparison from './Comparison';
import ComparisonAccordions from './ComparisonAccordions';
import { styled, alpha } from '@mui/material/styles';
import { colors } from '../../theme/color-palette';
import { UserPortalContext } from '../../context/appcontext';
import { Alert } from './Alerts';
import { Tooltip, HotSpot } from './FT-UX';
import { getStoredTooltipData } from './FT-UX/util';
import analytics from '../../helper/analytics';
import tracker from '../../constants/tracker';
import Loader from '../Loader/Loader';

const CreateRule = ({ drawserClose }) => {
	const {
		ruleType,
		previewMessage,
		createRuleErrorCode,
		hasRuleCreated,
		lastCreatedRuleName,
		previewLoading,
		createRuleLoading,
		editRuleData,
	} = useSelector((state) => state.createRuleReducer);

	const enableNotification =
		editRuleData?.notificationPreference?.length > 1 ? true : false;

	const classes = useStyles();
	const [itemType, setItemType] = useState(
		editRuleData
			? editRuleData?.conditionType === 'Compare'
				? COMPARISON
				: THRESHOLD
			: null
	);
	const [itemName, setItemName] = useState(
		editRuleData ? editRuleData?.name : null
	);
	const [enableRule, setEnableRule] = useState(true);
	const [thresholdData, setThresholdData] = useState({});
	const [comparisonData, setComparisonData] = useState({});
	const [checked, setChecked] = useState(
		editRuleData ? enableNotification : false
	);
	const [showErrorAlert, setShowErrorAlert] = useState(false);
	const [comparisonSelected, setComparisonSelected] = useState(
		editRuleData ? 'A lesser than B' : 'A greater than B'
	);
	const storedValue = getStoredTooltipData();
	const dispatch = useDispatch();

	const portalUserContext = useContext(UserPortalContext);

	const PurpleSwitch = styled(Switch)(({ theme }) => ({
		'&.MuiSwitch-root': {
			width: 25,
			height: 11,
		},
		'& .MuiSwitch-switchBase.Mui-checked': {
			color: colors.PURPLE,
			'&:hover': {
				backgroundColor: alpha(
					colors.PURPLE,
					theme.palette.action.hoverOpacity
				),
			},
		},
		'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
			backgroundColor: colors.PURPLE,
		},
	}));

	const isComparison = ruleType === COMPARISON;

	const handleChange = (event) => {
		const { value } = event.target;
		setItemType(value);
		dispatch(setRuleType(value));
	};

	const handleNameChange = (event) => {
		setItemName(event.target.value);
	};

	const getThresholdData = (data) => {
		const {
			aggregationLevel,
			threshold,
			productHierarchyType,
			measureType,
			metric,
			channels,
			channelType,
			locationFilters,
			location,
			productFilters,
			metricDomain,
			error,
			itemName,
		} = data;

		if (previewMessage) {
			dispatch(createRuleFailure(0));
		}

		if (
			channelType === 'STORE' &&
			location !== 'USA' &&
			(!locationFilters || locationFilters?.length < 1)
		) {
			setEnableRule(true);
			return;
		}

		if (
			metricDomain !== 'SALES' &&
			aggregationLevel &&
			threshold &&
			measureType &&
			channelType &&
			metric !== NEED_TO_BE_SELECTED &&
			productHierarchyType &&
			productFilters &&
			productFilters?.length > 0 &&
			itemName &&
			!error
		) {
			setEnableRule(false);
			setThresholdData(data);
			return;
		}

		if (
			aggregationLevel &&
			threshold &&
			measureType &&
			channelType &&
			channels?.length > 0 &&
			metric !== NEED_TO_BE_SELECTED &&
			productHierarchyType &&
			productFilters &&
			productFilters?.length > 0 &&
			itemName &&
			!error
		) {
			setEnableRule(false);
			setThresholdData(data);
			return;
		}
		setEnableRule(true);
	};

	const getComparisonData = (data) => {
		if (previewMessage) {
			dispatch(createRuleFailure(0));
		}

		const {
			aggregationLevel,
			productHierarchyType,
			measureType,
			metric,
			productFilters,
			metricDomain,
		} = data?.left || {};

		const {
			aggregationLevelB,
			productHierarchyTypeB,
			measureTypeB,
			metricB,
			productFiltersB,
			metricDomainB,
		} = data?.right || {};

		const { channelsA, channelTypeA, locationFiltersA, locationA } =
			data?.A || {};
		const { channelsB, channelTypeB, locationFiltersB, locationB } =
			data?.B || {};

		// Location is not usa for A
		if (
			channelTypeA === 'STORE' &&
			locationA !== 'USA' &&
			(!locationFiltersA || locationFiltersA?.length < 1)
		) {
			setEnableRule(true);
			return;
		}

		// Location is not usa for B
		if (
			channelTypeB === 'STORE' &&
			locationB !== 'USA' &&
			(!locationFiltersB || locationFiltersB?.length < 1)
		) {
			setEnableRule(true);
			return;
		}

		// A is sales and B is other
		if (
			metricDomain === 'SALES' &&
			aggregationLevel &&
			measureType &&
			locationA &&
			channelsA?.length > 0 &&
			metric !== NEED_TO_BE_SELECTED &&
			productHierarchyType &&
			productFilters &&
			productFilters?.length > 0 &&
			metricDomainB !== 'SALES' &&
			aggregationLevelB &&
			measureTypeB &&
			locationB &&
			metricB !== NEED_TO_BE_SELECTED &&
			productHierarchyTypeB &&
			productFiltersB &&
			productFiltersB?.length > 0
		) {
			setEnableRule(false);
			setComparisonData(data);
			return;
		}

		// A is other and B is sales
		if (
			metricDomain !== 'SALES' &&
			aggregationLevel &&
			measureType &&
			locationA &&
			metric !== NEED_TO_BE_SELECTED &&
			productHierarchyType &&
			productFilters &&
			productFilters?.length > 0 &&
			metricDomainB === 'SALES' &&
			aggregationLevelB &&
			measureTypeB &&
			locationB &&
			channelsB?.length > 0 &&
			metricB !== NEED_TO_BE_SELECTED &&
			productHierarchyTypeB &&
			productFiltersB &&
			productFiltersB?.length > 0
		) {
			setEnableRule(false);
			setComparisonData(data);
			return;
		}

		// A and B is other then we dont have channel options
		if (
			metricDomain !== 'SALES' &&
			aggregationLevel &&
			measureType &&
			locationA &&
			locationB &&
			metric !== NEED_TO_BE_SELECTED &&
			productHierarchyType &&
			productFilters &&
			productFilters?.length > 0 &&
			metricDomainB !== 'SALES' &&
			aggregationLevelB &&
			measureTypeB &&
			metricB !== NEED_TO_BE_SELECTED &&
			productHierarchyTypeB &&
			productFiltersB &&
			productFiltersB?.length > 0 &&
			itemName
		) {
			setEnableRule(false);
			setComparisonData(data);
			return;
		}

		// A and B is sales
		if (
			aggregationLevel &&
			measureType &&
			channelsA?.length > 0 &&
			channelTypeA &&
			locationA &&
			locationB &&
			metric !== NEED_TO_BE_SELECTED &&
			productHierarchyType &&
			productFilters &&
			productFilters?.length > 0 &&
			aggregationLevelB &&
			measureTypeB &&
			channelsB?.length > 0 &&
			channelTypeB &&
			metricB !== NEED_TO_BE_SELECTED &&
			productHierarchyTypeB &&
			productFiltersB &&
			productFiltersB?.length > 0
		) {
			setEnableRule(false);
			setComparisonData(data);
			return;
		}
		setEnableRule(true);
	};

	const setFilters = (data, location) => {
		if (location === 'STORES') {
			return data?.map((item) => item.storeId);
		}
		return data;
	};

	const setProductFilters = (type, data) => {
		const name = productFilters[type];
		return data?.map((item) => item[name]);
	};

	const handleSwitchChange = (event) => {
		setChecked(event.target.checked);
	};

	const getCrossDomain = (metricDomain, metricDomainB) => {
		if (
			(metricDomain === 'SALES' && metricDomainB === 'INVENTORY') ||
			(metricDomain === 'INVENTORY' && metricDomainB === 'SALES')
		) {
			return 'SALES_INVENTORY';
		} else {
			return metricDomain;
		}
	};

	const compaisionPayload = (type) => {
		const {
			aggregationLevel,
			measureType,
			metric,
			metricDomain,
			productHierarchyType,
			productFilters,
		} = comparisonData?.left || {};

		const {
			aggregationLevelB,
			measureTypeB,
			metricB,
			metricDomainB,
			productHierarchyTypeB,
			productFiltersB,
		} = comparisonData?.right || {};

		const { channelsA, channelTypeA, locationFiltersA, locationA } =
			comparisonData?.A || {};
		const { channelsB, channelTypeB, locationFiltersB, locationB } =
			comparisonData?.B || {};

		const request = {
			name: itemName,
			executionTypeForProduct: 'AGGREGATE',
			executionTypeForLocation: 'AGGREGATE',
			metricDomain: getCrossDomain(metricDomain, metricDomainB),
			left: {
				metric,
				measureType,
				aggregationLevel,
				channelType: channelTypeA,
				channels: channelsA,
				location: locationA,
				locationFilters: setFilters(locationFiltersA, locationA),
				productHierarchyType,
				productFilters: setProductFilters(productHierarchyType, productFilters),
			},
			right: {
				metric: metricB,
				measureType: measureTypeB,
				aggregationLevel: aggregationLevelB,
				channelType: channelTypeB,
				channels: channelsB,
				location: locationB,
				locationFilters: setFilters(locationFiltersB, locationB),
				productHierarchyType: productHierarchyTypeB,
				productFilters: setProductFilters(
					productHierarchyTypeB,
					productFiltersB
				),
			},
			operation:
				comparisonSelected === 'A greater than B'
					? 'GreaterThan'
					: 'LessThanEquals',
			conditionType: 'Compare',
			status: 'Active',
			notificationPreference: checked ? ['InApp', 'Email'] : ['InApp'],
		};

		if (editRuleData) {
			request.id = editRuleData?.id;
			request.parentCompany = editRuleData?.parentCompany;
			request.loginId = editRuleData?.loginId;
			request.lastUpdatedTimeStamp = editRuleData?.lastUpdatedTimeStamp;
			request.lastExecutedDate = editRuleData?.lastExecutedDate;
			request.fullName = editRuleData?.fullName;
			request.email = editRuleData?.email;
			request.createdTimeStamp = editRuleData?.createdTimeStamp;
		}

		dispatch(createRule({ portalUserContext, request, type }));

		if (type === 'create') {
			// Clears the selected items on the Modal while Creation of the Rule.
			[
				'store',
				'item',
				'category',
				'subcategory',
				'brand',
				'department',
				'upc',
			].forEach((type) => {
				dispatch(updateCompareSelectedItemA({ [type]: [] }));
				dispatch(updateCompareSelectedItemB({ [type]: [] }));
				dispatch(updateBulkItemSearchResults({ [type]: [] }));
				dispatch(updateSelectedItem({ [type]: [] }));
			});
		}
	};

	const handleCreateRule = (type) => {
		//AA : No of rules created of each type
		let actionOn = tracker.ACTION_ON.RULES_CREATED_THRESHOLD;
		if (enableRule) {
			return null;
		}
		if (itemType === COMPARISON) {
			actionOn = tracker.ACTION_ON.RULES_CREATED_COMPARISON;
			return compaisionPayload(type);
		}
		const {
			aggregationLevel,
			measureType,
			metric,
			location,
			channels,
			executionTypeForLocation,
			executionTypeForProduct,
			threshold,
			operation,
			conditionType,
			status,
			metricDomain,
			channelType,
			locationFilters,
			productHierarchyType,
			productFilters,
		} = thresholdData;

		const request = {
			name: itemName,
			executionTypeForLocation,
			executionTypeForProduct,
			metricDomain,
			left: {
				metric,
				measureType,
				aggregationLevel,
				channelType,
				channels,
				location,
				locationFilters: setFilters(locationFilters, location),
				productHierarchyType,
				productFilters: setProductFilters(productHierarchyType, productFilters),
			},
			threshold,
			operation,
			conditionType,
			status,
			notificationPreference: checked ? ['InApp', 'Email'] : ['InApp'],
		};

		// API Team will identify that its the edit part
		if (editRuleData) {
			request.id = editRuleData?.id;
			request.parentCompany = editRuleData?.parentCompany;
			request.loginId = editRuleData?.loginId;
			request.lastUpdatedTimeStamp = editRuleData?.lastUpdatedTimeStamp;
			request.lastExecutedDate = editRuleData?.lastExecutedDate;
			request.fullName = editRuleData?.fullName;
			request.email = editRuleData?.email;
			request.createdTimeStamp = editRuleData?.createdTimeStamp;
			if (itemType === COMPARISON) {
				actionOn = tracker.ACTION_ON.RULES_EDITED_COMPARISON;
			} else {
				actionOn = tracker.ACTION_ON.RULES_EDITED_THRESHOLD;
			}
		}

		dispatch(createRule({ portalUserContext, request, type }));
		if (type === 'create') {
			// Clears the selected items on the Modal while Creation of the Rule.
			[
				'store',
				'item',
				'category',
				'subcategory',
				'brand',
				'department',
				'upc',
			].forEach((type) => {
				dispatch(updateSelectedItem({ [type]: [] }));
				dispatch(updateBulkItemSearchResults({ [type]: [] }));
			});
		}
		//Adobe event : No of Rules Created of each type
		analytics.trackEventInfo({
			type: tracker.EVENTS.CLICK,
			actionOn: actionOn,
		});

		return null;
	};

	/**
	 * Close CreateAlert Drawer on API Success &
	 * redirects the user to WatchList(Rules) Page
	 */
	useEffect(() => {
		if (lastCreatedRuleName?.length > 0 && hasRuleCreated) {
			dispatch(setIsCreateRuleOpen(false));
			dispatch(setIsWatchListOpen(true));
		}
	}, [lastCreatedRuleName, hasRuleCreated]);

	/**
	 * Show Alert when Create Rule has error
	 */
	useEffect(() => {
		if (createRuleErrorCode !== 0) {
			setShowErrorAlert(true);
		}
	}, [createRuleErrorCode]);

	/**
	 * Hide Alert when User makes some keyboard/mouse activity
	 */
	const keyDownHandler = (e) =>
		e instanceof KeyboardEvent || e instanceof PointerEvent
			? setShowErrorAlert(false)
			: null;
	useEffect(() => {
		// Register the handler with events
		['keydown', 'click'].forEach((e) =>
			document.addEventListener(e, keyDownHandler)
		);
		return () =>
			['keydown', 'click'].forEach((e) =>
				document.removeEventListener(e, keyDownHandler)
			);
	}, []);

	return (
		<Box
			className={
				isComparison ? classes.comparisoncontainer : classes.container
			}>
			<Box className={classes.header}>
				<ArrowBackIosNewIcon
					className={classes.backBtn}
					onClick={drawserClose}
				/>
				<Box className={classes.headerText}>
					{editRuleData ? EDIT_RULE : CREATE_RULE}
				</Box>
			</Box>
			{/* Alert - Error, Contact support */}
			<Alert
				open={showErrorAlert}
				style={{ marginTop: '24px' }}
				content={
					<Fragment>
						{`We could not ${
							editRuleData ? 'edit' : 'create'
						} the rule at the moment`}
						<br />
						<a
							className={classes.supportLink}
							href="https://www.scintilla.com/support"
							target="_blank"
							rel="noreferrer">
							Contact support
						</a>
					</Fragment>
				}
			/>
			<Box
				className={isComparison ? classes.comparisoncontent : classes.content}>
				<TextField
					id="outlined-basic"
					label="Rule Name"
					value={itemName}
					onChange={handleNameChange}
					variant="outlined"
					inputProps={{ style: { ...styles.textField } }}
					InputLabelProps={{ style: { ...styles.textField } }}
					sx={textStyle}
					autoComplete="off"
				/>
				<Tooltip
					placement={LEFT_START}
					show={storedValue?.showNameAndAlertTooltip}
					type="nameAndType">
					<FormControl
						sx={
							isComparison
								? { ...styles.comparisonSelect, ...styles.outline }
								: { ...styles.itemTypeSelect, ...styles.outline }
						}
						style={{ marginBottom: '20px' }}>
						<InputLabel
							id="simple-select-helper-label"
							sx={{ ...styles.textField }}>
							Rule Type
						</InputLabel>
						<Select
							labelId="simple-select-helper-label"
							id="simple-select-helper"
							value={itemType}
							label="Rule type"
							disabled={editRuleData}
							onChange={handleChange}
							fullWidth
							sx={{
								width: 328,
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
							}}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							<MenuItem className={classes.menuItems} value={THRESHOLD}>
								{THRESHOLD}
							</MenuItem>
							<MenuItem className={classes.menuItems} value={COMPARISON}>
								{COMPARISON}
							</MenuItem>
						</Select>
					</FormControl>
				</Tooltip>

				{itemType === THRESHOLD && (
					<>
						<Threshold
							width={290}
							getData={getThresholdData}
							editRuleData={editRuleData}
							itemName={itemName}
						/>
						<Box className={classes.notifyContent}>
							<Typography sx={{ ...styles.notifyText }}>
								Notify me via email
							</Typography>
							<PurpleSwitch
								inputProps={{ 'aria-label': 'Switch Toggle' }}
								size="small"
								checked={checked}
								onChange={handleSwitchChange}
								disabled={enableRule}
							/>
						</Box>
					</>
				)}
				{itemType === COMPARISON && (
					<Comparison
						getComparisonSelected={setComparisonSelected}
						operation={editRuleData?.operation}
					/>
				)}
			</Box>
			{itemType === COMPARISON && (
				<>
					<ComparisonAccordions
						getData={getComparisonData}
						editRuleData={editRuleData}
						itemName={itemName}
					/>
					<Box className={classes.notifyContent}>
						<Typography
							sx={{ ...styles.notifyText }}
							style={{ marginLeft: '16px' }}>
							Notify me via email
						</Typography>
						<PurpleSwitch
							inputProps={{ 'aria-label': 'Switch Toggle' }}
							size="small"
							checked={checked}
							onChange={handleSwitchChange}
							disabled={enableRule}
						/>
					</Box>
				</>
			)}
			{previewLoading || createRuleLoading ? <Loader /> : null}
			{previewMessage && (
				<TextField
					id="preview-basic"
					value={previewMessage}
					inputProps={{ style: { ...styles.textField } }}
					InputLabelProps={{ style: { ...styles.textField } }}
					style={{ marginLeft: '15px', width: '90%', marginBottom: '30px' }}
					sx={textStyle}
					multiline
				/>
			)}

			<Box className={classes.addToWatchListBtnContainer}>
				{itemType && (
					<Box style={{ display: 'flex', justifyContent: 'center' }}>
						<Tooltip
							placement={LEFT_START}
							show={storedValue?.showPreviewAlertTooltip && !enableRule}
							type="previewAlert">
							{/* Hot spot for FT UX  */}

							<Button
								className={
									enableRule ? classes.addToWatchlistBtn : classes.previewBtn
								}
								style={{
									textTransform: 'none',
									marginBottom: '20px',
								}}
								onClick={() => handleCreateRule('preview')}>
								<HotSpot
									show={storedValue?.showPreviewAlertTooltip && !enableRule}
									left={'-222%'}
									top={'50%'}>
									Preview
								</HotSpot>
							</Button>
						</Tooltip>
					</Box>
				)}
				<Box style={{ display: 'flex', justifyContent: 'center' }}>
					<Tooltip
						placement={LEFT_START}
						show={storedValue?.showAllSetTooltip && previewMessage?.length > 0}
						type="allSet">
						<Button
							className={
								enableRule
									? classes.addToWatchlistBtn
									: classes.enableAddToWatchlist
							}
							style={{ textTransform: 'none' }}
							onClick={() => handleCreateRule('create')}>
							{editRuleData ? EDIT_RULE : CREATE_RULE}
						</Button>
					</Tooltip>
				</Box>
			</Box>
		</Box>
	);
};

CreateRule.propTypes = {
	drawserClose: PropTypes.func,
};

export default CreateRule;
